import React, { useState, useEffect, useMemo } from "react";
import debounce from 'lodash/debounce';
import Select, { createFilter } from "react-select";
import { useTheme } from "../hooks/useTheme";
import SkeletonSelect from "./skeletons/SkeletonSelect";
import { Row, Col, FormGroup, Label, Input } from "reactstrap";
import CustomMenuList from '../components/utility/CustomMenuList';

export const SelectInput = (props) => {
    const theme = useTheme();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const t = setTimeout(() => setIsLoading(false), 2000);
        return () => clearTimeout(t);
    }, []);

    const sortedOptions = Array.isArray(props.options[0]?.options)
        ? props.options.map(g => ({
            ...g,
            options: [...g.options].sort((a, b) =>
                a.label.toLowerCase().localeCompare(b.label.toLowerCase())
            ),
        }))
        : [...props.options].sort((a, b) =>
            a.label.toLowerCase().localeCompare(b.label.toLowerCase())
        );

    const flatOptions = Array.isArray(sortedOptions[0]?.options)
        ? sortedOptions.flatMap(g => g.options)
        : sortedOptions;

    const coerceToOption = (val) => {
        if (val == null) return null;
        if (typeof val === "object" && "value" in val) {
            return flatOptions.find(o => o.value === val.value) ?? val;
        }
        return flatOptions.find(o => o.value === val) ?? null;
    };

    const selectionProps = useMemo(() => {
        const p = {};
        const hasValue = props.value != null;        
        const hasDefault = props.defaultValue != null;

        if (hasValue) {
            p.value = coerceToOption(props.value);
        } else if (hasDefault) {
            p.defaultValue = coerceToOption(props.defaultValue);
        }
        return p;
    }, [props.value, props.defaultValue, flatOptions]);

    const customTheme = {
        ...theme,
        colors: {
            ...theme.colors,
            primary25: "#B2D953",
            primary: theme === "dark-mode" ? "#2c414b" : "#dee2e6",
            neutral0: theme === "dark-mode" ? "#1b272d" : "#E4E4E4",
        },
    };

    const selectStyle = props.size === "small" ? {
        control: (base, state) => ({
            ...base,
            minHeight: '32px',
            height: '32px',
            fontSize: '0.875rem', // text-sm
            borderRadius: '0.375rem', // rounded-md
            borderColor: state.isFocused ? '#3b82f6' : '#d1d5db', // tailwind blue-500 or gray-300
            boxShadow: 'none',
            '&:hover': {
                borderColor: '#3b82f6',
            },
        }),
        option: (styles, { isSelected }) => ({
            ...styles,
            fontFamily: "Hanken Grotesk, Sans-serif",
            fontWeight: isSelected ? "bold" : "normal",
            color: isSelected ? "#fff" : "",
            lineHeight: "0.8em",
        }),
    } : {
        option: (styles, { isSelected }) => ({
            ...styles,
            fontFamily: "Hanken Grotesk, Sans-serif",
            fontWeight: isSelected ? "bold" : "normal",
            color: isSelected ? "#fff" : "",
            lineHeight: "0.8em",
        }),
    };

    if (props.advanced) {
        (selectStyle).container = (provided) => ({ ...provided, width: "100%" });
    }

    if (!props.options || props.options.length === 0) {
        return isLoading ? <SkeletonSelect /> : <p>No options available</p>;
    }

    return (
        <Col style={{ minWidth: "170px" }}>
            <Select
                id={props.id}
                data-testid={props.testId}
                isSearchable={props.isSearchable || false}
                {...selectionProps}
                onChange={(opt) => props.onChange?.(opt?.value)}
                filterOption={props.isSearchable ? createFilter({ ignoreAccents: false }) : undefined}
                options={sortedOptions}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                    ...selectStyle,
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
                classNamePrefix="custom-select data-select"
                theme={customTheme}
                placeholder={props.placeholder || `Select ${props.label}`}
                isDisabled={props.isDisabled || false}
                isClearable={props.isClearable !== undefined ? props.isClearable : true}
                captureMenuScroll={false}
                components={{ MenuList: CustomMenuList }}
            />
        </Col>
    );
};

const SwitchInput = (props) => {
    const [checked, setChecked] = useState(props.defaultValue || false);

    const handleChange = (event) => {
        if (props.onChange) {
            setChecked(event.target.checked);
            props.onChange(event.target.checked);
        }
    };

    return (
        <>
            <Col style={{ width: "210px" }}>
                <FormGroup switch>
                    <Input id="switch" type="switch" role="switch" step="1" checked={checked} onChange={handleChange}></Input>
                </FormGroup>
            </Col>
        </>
    )
}

const TextInput = (props) => {
    const handleChange = (event) => {
        if (props.onChange) {
            debouncedTextInputChange(event.target.value);
        }
    };

    const debouncedTextInputChange = useMemo(() =>
        debounce((value) => {
            props.onChange(value || "");
        }, 500), [props.onChange]
    );

    useEffect(() => {
        return () => {
            debouncedTextInputChange.cancel();
        };
    }, [debouncedTextInputChange]);

    return (
        <Input id="text" type="text" size="sm" onChange={handleChange}></Input>
    )
}

const DateTimeInput = (props) => {
    const handleDateTimeChange = (event) => {
        if (props.onChange) {
            props.onChange(event.target.value || "");
        }
    };

    return (
        <Input id="timestamp" type="datetime-local" size="sm" step="1" onChange={handleDateTimeChange}></Input>
    )
}

const DateRangeInput = (props) => {
    const [value, setValue] = useState(props.defaultValue || { from: "", to: "" });

    useEffect(() => {
        if (props.onChange) {
            props.onChange(value);
        }
    }, [value]);

    const handleDateTimeChange = (event) => {
        debouncedDateRangeInputChange(event);
    };

    const debouncedDateRangeInputChange = useMemo(() =>
        debounce((event) => {
            setValue((prev) => {
                return { ...prev, [event.target.name]: event.target.value }
            })
        }, 1500)
    );

    useEffect(() => {
        return () => {
            debouncedDateRangeInputChange.cancel();
        };
    }, [debouncedDateRangeInputChange]);

    return (
        <>
            <Row>
                <Col sm="6">
                    <FormGroup row>
                        <Col sm={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Label style={{ marginBottom: 0 }} for="from">
                                From
                            </Label>
                        </Col>
                        <Col sm={10}>
                            <Input
                                id="from"
                                name="from"
                                type="datetime-local"
                                size="sm"
                                step="1"
                                onChange={handleDateTimeChange}
                                defaultValue={value.from || undefined}
                            />
                        </Col>
                    </FormGroup>
                </Col>
                <Col sm="6">
                    <FormGroup row>
                        <Col sm={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Label style={{ marginBottom: 0 }} for="to">
                                To
                            </Label>
                        </Col>
                        <Col sm={10}>
                            <Input
                                id="to"
                                name="to"
                                type="datetime-local"
                                size="sm"
                                step="1"
                                onChange={handleDateTimeChange}
                                defaultValue={value.to || undefined}
                            />
                        </Col>
                    </FormGroup>
                </Col>
            </Row>
        </>
    )
}

const NumberRangeInput = (props) => {
    const [value, setValue] = useState({ from: "", to: "" });

    useEffect(() => {
        if (props.onChange) {
            props.onChange(value);
        }
    }, [value]);

    const handleNumberRangeChange = (event) => {
        debouncedNumberRangeInputChange(event);
    };

    const debouncedNumberRangeInputChange = useMemo(() =>
        debounce((event) => {
            setValue((prev) => {
                return { ...prev, [event.target.name]: event.target.value }
            })
        }, 500)
    );

    useEffect(() => {
        return () => {
            debouncedNumberRangeInputChange.cancel();
        };
    }, [debouncedNumberRangeInputChange]);

    return (
        <>
            <Row>
                <Col sm="6">
                    <FormGroup row style={{ alignItems: 'center' }}>
                        <Col sm={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Label style={{ marginBottom: 0 }} for="from">
                                From
                            </Label>
                        </Col>
                        <Col sm={10}>
                            <Input
                                id="from"
                                name="from"
                                type="number"
                                size="sm"
                                step={props.step}
                                onChange={handleNumberRangeChange}
                            />
                        </Col>
                    </FormGroup>
                </Col>
                <Col sm="6">
                    <FormGroup row style={{ alignItems: 'center' }}>
                        <Col sm={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Label style={{ marginBottom: 0 }} for="to">
                                To
                            </Label>
                        </Col>
                        <Col sm={10}>
                            <Input
                                id="to"
                                name="to"
                                type="number"
                                size="sm"
                                step={props.step}
                                onChange={handleNumberRangeChange}
                            />
                        </Col>
                    </FormGroup>
                </Col>
            </Row>
        </>
    )
}

const DataFilter = (props) => {
    return (
        <>
            <span>{props.advanced}</span>
            {props.advanced &&
                <div style={{ width: '100%' }}>
                    <FormGroup row className="w-100 mx-2">
                        <Label for={props.id} sm={3} className="text-start">
                            {props.label}
                        </Label>
                        <Col sm={9}>
                            {props.type === "datetime" && <DateTimeInput {...props} />}
                            {props.type === "daterange" && <DateRangeInput {...props} />}
                            {props.type === "select" && <SelectInput {...props} />}
                            {props.type === "switch" && <SwitchInput {...props} />}
                            {props.type === "text" && <TextInput {...props} />}
                            {props.type === "numberrange" && <NumberRangeInput {...props} />}
                        </Col>
                    </FormGroup>
                </div>
            }

            {!props.advanced &&
                <>
                    {(props.type === "switch") &&
                        <Label for="timestamp">
                            {props.label}
                        </Label>
                    }
                    <Col style={{ minWidth: "170px" }}>
                        {props.type === "datetime" && <DateTimeInput {...props} />}
                        {props.type === "daterange" && <DateRangeInput {...props} />}
                        {props.type === "select" && <SelectInput {...props} />}
                        {props.type === "switch" && <SwitchInput {...props} />}
                        {props.type === "text" && <TextInput {...props} />}
                        {props.type === "numberrange" && <NumberRangeInput {...props} />}
                    </Col>
                </>
            }
        </>
    );
};

export default DataFilter;
